import { useState, useEffect, useCallback } from 'react';
import dayjs from 'utils/dayjs';
import { EAppointmentContactTypeDictKey } from 'types/common';
import { getUpcomingAppointments } from 'api/home';
import { TUpcomingAppointment, TAppointmentAddress } from 'types/home';

interface TodayAppointmentItem {
    id: number;
    time: string;
    appointmentName: string;
    patientName: string;
    patientAvatar?: string;
    startAt: string;
    endAt: string;
    duration: number;
    contactType: EAppointmentContactTypeDictKey;
    externalVideoLink?: string;
    location?: string;
    clientId: number;
}

const useGetTodayAppointments = (): [TodayAppointmentItem[], boolean] => {
    const [appointments, setAppointments] = useState<TodayAppointmentItem[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchTodayAppointments = useCallback(async () => {
        setLoading(true);
        try {
            const today = dayjs();
            const start = today.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
            const end = today.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ');

            const response = await getUpcomingAppointments({ start, end });
            if (response && !response.error) {
                const data = response?.data?.data;
                if (data && data.list) {
                    const now = dayjs();
                    const todayAppointments: TodayAppointmentItem[] = data.list
                        .filter((appointment: TUpcomingAppointment) => {
                            if (!appointment.startAt) {
                                return false;
                            }
                            const eventDate = dayjs(appointment.startAt);
                            // 只显示今天的预约，且是未来的时间
                            return eventDate.isSame(today, 'day') && eventDate.isAfter(now);
                        })
                        .map((appointment: TUpcomingAppointment) => {
                            const startAt = dayjs(appointment.startAt);
                            const endAt = dayjs(appointment.endAt);
                            const duration = appointment.duration || Math.round(endAt.diff(startAt, 'minute'));

                            const contactType = appointment.contactType === 'video' || appointment.contactType === 'VIDEO'
                                ? EAppointmentContactTypeDictKey.VIDEO
                                : EAppointmentContactTypeDictKey.IN_PERSON;

                            // 获取默认地址或第一个地址作为位置信息
                            const defaultAddress = appointment.address?.find((addr: TAppointmentAddress) => addr.isDefault) || appointment.address?.[0];
                            const location = appointment.location || (defaultAddress
                                ? `${defaultAddress.practiceAddress}, ${defaultAddress.practiceCity}, ${defaultAddress.practiceState} ${defaultAddress.practiceZip}`
                                : undefined);

                            return {
                                id: appointment.id,
                                time: startAt.format('h:mm A'),
                                appointmentName: appointment.appointmentName,
                                patientName: appointment.clientName,
                                patientAvatar: undefined, // API 返回中没有头像字段
                                startAt: appointment.startAt,
                                endAt: appointment.endAt,
                                duration,
                                contactType,
                                externalVideoLink: appointment.externalVideoLink || undefined,
                                location,
                                clientId: appointment.clientId,
                            };
                        })
                        .sort((a: TodayAppointmentItem, b: TodayAppointmentItem) => {
                            return dayjs(a.startAt).diff(dayjs(b.startAt));
                        });

                    setAppointments(todayAppointments);
                }
            }
        } catch (error) {
            console.error('Failed to fetch today appointments:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTodayAppointments();
    }, [fetchTodayAppointments]);

    return [appointments, loading];
};

export default useGetTodayAppointments;
